import { ArrowUpRight, X } from "@phosphor-icons/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect } from "react";

const venues = [
  {
    name: "Fomo",
    label: "Trade on Fomo",
    href: "https://fomo.family/coin?address=HcRLc9VDgjLeK154xDawfb1dmVJ98DoSqcwTHGqiDeJR&chainId=1399811149&r=ImDanTheMan&source=share_link",
  },
  {
    name: "Pump",
    label: "Trade on Pump",
    href: "https://pump.fun/callouts/HcRLc9VDgjLeK154xDawfb1dmVJ98DoSqcwTHGqiDeJR/71f0d543-741d-44a6-b6f7-93f688ad2935",
  },
  {
    name: "Axiom",
    label: "Trade on Axiom",
    href: "https://axiom.trade/t/HcRLc9VDgjLeK154xDawfb1dmVJ98DoSqcwTHGqiDeJR/@dantheman",
  },
] as const;

function VenueMark({ name }: { name: (typeof venues)[number]["name"] }) {
  if (name === "Fomo") {
    return <svg viewBox="0 0 48 48" role="img" aria-label="Fomo"><path d="M6.5 27.5c0-7 3.8-11 9.4-11 4.1 0 6.9 2.2 8.1 6 1.2-3.8 4-6 8.1-6 5.6 0 9.4 4 9.4 11s-3.8 11-9.4 11c-4.1 0-6.9-2.2-8.1-6-1.2 3.8-4 6-8.1 6-5.6 0-9.4-4-9.4-11Zm7.1 0c0 3.3 1.4 5 3.8 5 2.3 0 3.7-1.7 3.7-5s-1.4-5-3.7-5c-2.4 0-3.8 1.7-3.8 5Zm13.3 0c0 3.3 1.4 5 3.7 5 2.4 0 3.8-1.7 3.8-5s-1.4-5-3.8-5c-2.3 0-3.7 1.7-3.7 5Z" /></svg>;
  }
  if (name === "Pump") {
    return <svg viewBox="0 0 48 48" role="img" aria-label="Pump"><g transform="rotate(-42 24 24)"><rect x="13" y="4.5" width="22" height="39" rx="11" fill="#f5f5f0" stroke="currentColor" strokeWidth="2.5" /><path d="M13 24h22v8.5c0 6.1-4.9 11-11 11s-11-4.9-11-11V24Z" fill="#54d58c" /><path d="M13 24h22" stroke="currentColor" strokeWidth="2.5" /></g></svg>;
  }
  return <svg viewBox="0 0 48 48" role="img" aria-label="Axiom"><path d="M24 5 43 40H5L24 5Zm0 12.4-9.4 17.3h18.8L24 17.4Z" fillRule="evenodd" /><path d="M15 27h18" stroke="#11110f" strokeWidth="3" /></svg>;
}

type TradeChooserProps = {
  open: boolean;
  onClose: () => void;
};

export function TradeChooser({ open, onClose }: TradeChooserProps) {
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="trade-backdrop"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.section
            className="trade-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trade-title"
            initial={reduced ? false : { opacity: 0, y: 24, scale: .98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: .98 }}
            transition={{ duration: .22, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="trade-dialog-head">
              <div>
                <p className="eyebrow">Choose your venue</p>
                <h2 id="trade-title">GET THE BAG.</h2>
              </div>
              <button className="trade-close" onClick={onClose} aria-label="Close buy options" autoFocus>
                <X size={22} weight="bold" />
              </button>
            </div>

            <div className="trade-options">
              {venues.map((venue) => (
                <a
                  className="trade-option"
                  href={venue.href}
                  target="_blank"
                  rel="noreferrer"
                  key={venue.name}
                  onClick={onClose}
                >
                  <span className="trade-logo"><VenueMark name={venue.name} /></span>
                  <strong>{venue.label}</strong>
                  <ArrowUpRight className="trade-arrow" size={22} weight="bold" />
                </a>
              ))}
            </div>

            <p className="trade-footnote">You’re leaving this site to trade on a third-party platform. Verify the contract before connecting a wallet.</p>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
